import { TestStep } from '../models/test-case.model';
import { TdbOverride } from './tcdl-ops';

/**
 * Vollstaendiges TDB-Override eines MODULE_REF-Schritts, wie der Modul-Drawer es
 * ans aufrufende Formular zurueckgibt (NEX-1052).
 *
 * <p>Der Typ stand vorher dreimal als Inline-Literal im Code (Drawer-Output,
 * Testfall-Formular, Modul-Formular) — sgraetz-Review NEX-1052.
 */
export interface TdbOverrideDelta {
  stepId: string;
  dataSheetId: string | null;
  dataSheetFilters: Record<string, string>[] | null;
  stepCondition: Record<string, string> | null;
  /**
   * Halbe Eingabe aus dem Drawer. `stepCondition` behaelt dann die zuletzt vollstaendige
   * Bedingung, deshalb braucht die Zeile den Entwurf, um die Sperre zu halten.
   */
  override?: TdbOverride;
}

/** Traegt das Override in den adressierten Schritt ein; alle anderen bleiben identisch. */
export function applyTdbOverride(steps: readonly TestStep[], delta: TdbOverrideDelta): TestStep[] {
  return steps.map((step) =>
    step.id === delta.stepId
      ? withFirstStepCondition(
          { ...step, dataSheetId: delta.dataSheetId, dataSheetFilters: delta.dataSheetFilters },
          delta.stepCondition,
        )
      : step,
  );
}

/**
 * Setzt die erste Bedingung; eine schon geladene Liste behaelt dabei die uebrigen, sonst
 * ersetzte die Seitenleiste mit ihrer einen Bedingung still die ganze Liste.
 */
export function withFirstStepCondition(step: TestStep, condition: Record<string, string> | null): TestStep {
  if (!step.stepConditions) return { ...step, stepCondition: condition };
  const rest = step.stepConditions.slice(1);
  return { ...step, stepCondition: condition, stepConditions: condition ? [condition, ...rest] : rest };
}

/**
 * Uebernimmt die erste Bedingung je Schritt aus der Antwort des Speicherns (NEX-2254).
 * Entfernt der Nutzer die erste von zwei Bedingungen, rueckt die zweite auf. Behielte das
 * offene Formular seinen alten Stand, loeschte das naechste Speichern die aufgerueckte still.
 */
export function withSavedStepConditions(
  steps: readonly TestStep[],
  saved: readonly TestStep[] | undefined,
): TestStep[] {
  const byId = new Map((saved ?? []).map((s) => [s.id, s]));
  return steps.map((step) => {
    const match = byId.get(step.id);
    if (!match) return step;
    const list = match.stepConditions === undefined ? {} : { stepConditions: match.stepConditions };
    return { ...step, stepCondition: match.stepCondition ?? null, ...list };
  });
}

/** Eine während des Speicherns geänderte Bedingung ist neuer als die Antwort und darf nicht überschrieben werden. */
export function savedConditionsNotChangedSince(
  current: readonly TestStep[],
  sent: readonly TestStep[],
  saved: readonly TestStep[] | undefined,
): TestStep[] {
  const currentById = new Map(current.map((s) => [s.id, s]));
  const sentById = new Map(sent.map((s) => [s.id, s]));
  return (saved ?? []).filter((s) => conditionsKey(currentById.get(s.id)) === conditionsKey(sentById.get(s.id)));
}

function conditionsKey(step: TestStep | undefined): string {
  return JSON.stringify([conditionKey(step?.stepCondition), (step?.stepConditions ?? []).map(conditionKey)]);
}

function conditionKey(condition: Record<string, string> | null | undefined): string {
  return JSON.stringify(Object.entries(condition ?? {}).sort(([a], [b]) => a.localeCompare(b)));
}
